import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import dayjs, { BusinessHoursMap } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import dayjsBusinessTime from 'dayjs-business-time';
import moment, { Moment } from 'moment';
import { ORDER_METHODS, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import { StoreInterface } from '../../store/types';
import { CompanyDetailsInterface, ScheduledOrderSlotInterface } from '../../services/exports/Interfaces';
import { useGetBusySlotsQuery, useGetHolidaysQuery } from '../../store/api-slices';

dayjs.extend(dayjsBusinessTime);
dayjs.extend(utc);

interface OrderSlotsProps {
  orderMethod: ORDER_METHODS;
  startDate: string;
  endDate?: string;
  date?: Moment;
  disabled?: boolean;
  company?: CompanyDetailsInterface;
}

const useOrderSlots = (props: OrderSlotsProps) => {
  const now = dayjs();
  const defaultCompany = useSelector((store: StoreInterface) => store.initialData.company);
  const company = props?.company ?? defaultCompany;
  const maxPreOrderDays =
    useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.max_pre_order_days) ?? 0;
  const lastOrderGapPickup = useSelector(
    (state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.last_order_gap_pickup,
  );
  const lastOrderGapDelivery = useSelector(
    (state) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.last_order_gap_delivery,
  );
  const minScheduleAheadDelivery = useSelector(
    (state) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.min_schedule_ahead_time_delivery,
  );
  const minScheduleAheadPickup = useSelector(
    (state) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.min_schedule_ahead_time_pickup,
  );
  const scheduleAheadTime =
    props.orderMethod === ORDER_METHODS.DELIVERY ? minScheduleAheadDelivery : minScheduleAheadPickup;
  const orderGap = props.orderMethod === ORDER_METHODS.DELIVERY ? lastOrderGapDelivery : lastOrderGapPickup;
  const isWithinMaxPreOrderDays = (date: Moment): boolean => {
    return !date || date.isSameOrBefore(now.clone().add(maxPreOrderDays, 'days').toISOString(), 'days');
  };
  const normalizedDate = useMemo(() => {
    const isToday = props.date && props.date.isSame(now.toISOString(), 'day');

    if (!props.date || isToday || !isWithinMaxPreOrderDays(props.date)) {
      return now.clone().add(scheduleAheadTime, 'minute');
    }

    return dayjs(props.date.toISOString()).startOf('day');
  }, [props.date, scheduleAheadTime]);
  const updateSlotsDatePattern = normalizedDate.format('YYYY-MM-DD');
  const companyId = company?.id;
  const workSchedule =
    props.orderMethod === ORDER_METHODS.DELIVERY ? company?.delivery_schedule : company?.work_schedule;
  const timeSlotInterval = useSelector(
    (state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.scheduled_orders_time_slot_interval,
  );
  const userTimezone = moment.tz.guess();
  const skipGetBusySlotsQuery = props.disabled || !companyId || !props.orderMethod;
  const skipGetHolidaysQuery = props.disabled || !companyId || !props.orderMethod || !props.startDate;
  const normalizedStartDate =
    props.startDate && isWithinMaxPreOrderDays(moment(props.startDate))
      ? props.startDate
      : normalizedDate.format('YYYY-MM');
  const endDate = props.endDate ?? now.clone().add(maxPreOrderDays, 'days').format('YYYY-MM');
  const { data: holidays, isLoading: isHolidaysLoading } = useGetHolidaysQuery(
    {
      companyId,
      params: { order_method: props.orderMethod, start_date: normalizedStartDate, end_date: endDate },
    },
    { skip: skipGetHolidaysQuery },
  );
  const { data: busySlots, isLoading: isBusySlotsLoading } = useGetBusySlotsQuery(
    {
      companyId,
      params: { order_method: props.orderMethod, date: updateSlotsDatePattern },
    },
    { skip: skipGetBusySlotsQuery },
  );
  const isSlotsLoading = isBusySlotsLoading || isHolidaysLoading;

  useMemo(() => {
    if (props.disabled) {
      return;
    }

    const businessTimes: BusinessHoursMap = {
      monday: null,
      tuesday: null,
      wednesday: null,
      thursday: null,
      friday: null,
      saturday: null,
      sunday: null,
    };

    const weekStart = moment()
      .tz(workSchedule?.[0]?.timezone ?? company?.timezone ?? 'Europe/Berlin')
      .startOf('week');

    for (const entry of workSchedule) {
      const openMoment = weekStart
        .clone()
        .add(entry.open.day, 'days')
        .hour(entry.open.hour)
        .minute(entry.open.minute)
        .second(0)
        .millisecond(0)
        .tz(userTimezone)
        .add(scheduleAheadTime, 'minute');
      const closeMoment = weekStart
        .clone()
        .add(entry.close.day, 'days')
        .hour(entry.close.hour)
        .minute(entry.close.minute)
        .second(0)
        .millisecond(0)
        .tz(userTimezone)
        .subtract(orderGap, 'minute');

      const openKey = openMoment.locale('en').format('dddd').toLowerCase();
      const closeKey = closeMoment.format('dddd').toLowerCase();
      const start = openMoment.format('HH:mm:ss');
      const end = closeMoment.format('HH:mm:ss');

      if (openKey === closeKey) {
        businessTimes[openKey] ??= [];
        businessTimes[openKey].push({ start, end });
        continue;
      }

      businessTimes[openKey] ??= [];
      businessTimes[openKey].push({ start, end: '23:59:59' });

      businessTimes[closeKey] ??= [];
      businessTimes[closeKey].push({ start: '00:00:00', end });
    }

    dayjs.setBusinessTime(businessTimes);

    if (!holidays?.length) {
      return;
    }

    dayjs.setHolidays(holidays);
  }, [workSchedule, userTimezone, holidays, props.disabled, scheduleAheadTime, orderGap, company]);

  const orderSlots = useMemo((): ScheduledOrderSlotInterface[] => {
    if (isBusySlotsLoading || props.disabled || skipGetHolidaysQuery) {
      return [];
    }

    const businessDay = normalizedDate.isBusinessDay() ? normalizedDate : normalizedDate.nextBusinessTime();
    const dayKey = businessDay.format('dddd').toLowerCase();
    const schedule = dayjs.getBusinessTime();
    const intervals = schedule[dayKey] || [];
    const slots: ScheduledOrderSlotInterface[] = [];

    intervals.forEach(({ start, end }) => {
      const startTimeline = moment(start, 'HH:mm:ss');
      const roundedMinutes = Math.ceil(startTimeline.minutes() / 5) * 5;

      const endTimeline = moment(end, 'HH:mm:ss');
      const endTime = businessDay
        .clone()
        .hour(endTimeline.hour())
        .minute(endTimeline.minute())
        .second(0)
        .millisecond(0);
      let cursor = businessDay.clone().hour(startTimeline.hour()).minute(roundedMinutes).second(0).millisecond(0);

      while (cursor.isBefore(endTime)) {
        const nextCursor = cursor.clone().add(timeSlotInterval, 'minute');
        const isBusySlot = busySlots[cursor.utc().format()];
        const isValidSlot = cursor.isAfter(businessDay) && !isBusySlot;

        if (isValidSlot) {
          slots.push({
            start_time: cursor.toISOString(),
            end_time: nextCursor.toISOString(),
          });
        }

        cursor = nextCursor;
      }
    });

    return slots;
  }, [
    dayjs.getHolidays(),
    dayjs.getBusinessTime(),
    timeSlotInterval,
    busySlots,
    updateSlotsDatePattern,
    isBusySlotsLoading,
    props.disabled,
    skipGetHolidaysQuery,
  ]);
  const isHoliday = (date: string) => dayjs(date).isHoliday();
  const isBusinessTime = (date: string): boolean => {
    if (!isWithinMaxPreOrderDays(moment(date))) {
      return;
    }

    return dayjs(date).isBusinessTime();
  };
  const isBusinessDay = (date: string): boolean => {
    if (!isWithinMaxPreOrderDays(moment(date))) {
      return;
    }

    if (!dayjs(date).isBusinessDay()) {
      return;
    }

    if (dayjs(date).isBusinessTime()) {
      return true;
    }

    return dayjs(date).nextBusinessTime().isSame(date, 'day');
  };
  const getNextBusinessTime = (date: string) => {
    if (!isWithinMaxPreOrderDays(moment(date))) {
      return normalizedDate.nextBusinessTime().toISOString();
    }

    return dayjs(date).nextBusinessTime().toISOString();
  };

  return {
    orderSlots,
    isSlotsLoading,
    holidays,
    isHolidaysLoading,
    maxPreOrderDays,
    isHoliday,
    isBusinessTime,
    getNextBusinessTime,
    isBusinessDay,
  };
};

export default useOrderSlots;
